import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { PillButton, PillInput } from '@/components/FormField';
import {
  clearRememberedCreds,
  readRememberFlag,
  readRememberedCreds,
  saveRememberedCreds,
} from '@/lib/remember';

const SIGNUP_URL = 'https://www.shiftpal.ca/plans-pricing';

export function LoginPage() {
  const signIn = useAuthStore((s) => s.signIn);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const autoTried = useRef(false);

  // On mount: prefill from stored creds and (if we have them) attempt a
  // silent sign-in so the user lands straight in the app. Only runs once.
  useEffect(() => {
    if (autoTried.current) return;
    autoTried.current = true;
    setRemember(readRememberFlag() || true);
    const creds = readRememberedCreds();
    if (!creds) return;
    setEmail(creds.email);
    setPassword(creds.password);
    setSubmitting(true);
    signIn(creds.email, creds.password)
      .catch(() => {
        // Stored password is stale — leave the form pre-filled for manual retry.
      })
      .finally(() => setSubmitting(false));
  }, [signIn]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      if (remember) saveRememberedCreds({ email, password });
      else clearRememberedCreds();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la connexion');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-dvh bg-brand-darkest text-white flex flex-col items-center justify-center px-6">
      <h1 className="text-3xl font-semibold mb-8">ShiftPal</h1>
      <form onSubmit={onSubmit} className="w-full max-w-sm flex flex-col gap-4">
        <PillInput
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          placeholder="Courriel"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PillInput
          type="password"
          autoComplete="current-password"
          required
          placeholder="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-white/80 px-2">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="accent-brand-orange"
          />
          Se souvenir de moi
        </label>
        {error && <p className="text-sm text-red-300 text-center">{error}</p>}
        <PillButton type="submit" disabled={submitting}>
          {submitting ? 'Connexion…' : 'Se connecter'}
        </PillButton>
        <div className="flex flex-col items-center gap-2 mt-2 text-sm text-white/80">
          <a
            href={SIGNUP_URL}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Créer un compte sur shiftpal.ca
          </a>
          <Link to="/forgot-password" className="underline">
            Mot de passe oublié ?
          </Link>
        </div>
      </form>
    </div>
  );
}
